import type { MetricWithSeries } from "@/shared/api/communication-stats"
import type { ChartData, ChartOptions } from "chart.js"
import { ru } from "date-fns/locale"
import { Line } from "react-chartjs-2"
import type { MetricPresentation } from "./metric-presentations"

interface CommunicationMetricChartProps {
    metric: MetricWithSeries
    presentation: MetricPresentation
}

export const buildMetricChartData = (
    metric: MetricWithSeries,
    presentation: MetricPresentation,
): ChartData<"line"> => ({
    labels: metric.series.map((point) => point.date),
    datasets: [{
        label: presentation.unit,
        data: metric.series.map((point) => point.count),
        borderColor: presentation.color,
        backgroundColor: presentation.color,
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.2,
    }],
})

export const buildMetricChartOptions = (unit: string): ChartOptions<"line"> => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
        legend: { display: false },
    },
    scales: {
        x: {
            type: "time",
            time: {
                unit: "day",
                round: "day",
                displayFormats: {
                    day: "dd.MM",
                },
                tooltipFormat: "d MMMM yyyy",
            },
            adapters: {
                date: { locale: ru },
            },
            ticks: {
                autoSkip: true,
                maxRotation: 0,
            },
            title: {
                display: true,
                text: "Дата",
            },
        },
        y: {
            beginAtZero: true,
            ticks: {
                precision: 0,
            },
            title: {
                display: true,
                text: unit,
            },
        },
    },
})

export const CommunicationMetricChart = ({ metric, presentation }: CommunicationMetricChartProps) => (
    <div
        className="mt-5 h-72 w-full min-w-0 overflow-hidden"
        data-testid={`metric-chart-${presentation.key}`}
        aria-label={`Динамика: ${presentation.title}, последние 90 суток, шаг — сутки`}
    >
        <Line
            data={buildMetricChartData(metric, presentation)}
            options={buildMetricChartOptions(presentation.unit)}
            role="img"
            aria-label={`График: ${presentation.title}`}
        />
        <table className="sr-only">
            <caption>{`Данные графика: ${presentation.title}`}</caption>
            <thead>
                <tr><th>Дата</th><th>{presentation.unit}</th></tr>
            </thead>
            <tbody>
                {metric.series.map((point) => (
                    <tr key={point.date}><td>{point.date}</td><td>{point.count}</td></tr>
                ))}
            </tbody>
        </table>
    </div>
)
